import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createTailoredSupplyClient } from './api';
import type {
  TailoredSupplyAppsFilter,
  TailoredSupplyCreatePayload,
  TailoredSupplyDomainsFilter,
  TailoredSupplyListFilter,
  TailoredSupplyLookupFilter,
} from './model';

export const tailoredSupplyQueryKey = ['supply', 'tailored-supply'] as const;
export const tailoredSupplyListsQueryKey = [...tailoredSupplyQueryKey, 'list'] as const;

function useTailoredSupplyClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createTailoredSupplyClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useTailoredSupplyListQuery(filter: TailoredSupplyListFilter) {
  const client = useTailoredSupplyClient();
  return useQuery({
    queryKey: [...tailoredSupplyListsQueryKey, filter],
    queryFn: () => client.listTailoredSupplies(filter),
  });
}

export function useTargetingTailoredSupplyQuery(filter: TailoredSupplyLookupFilter) {
  const client = useTailoredSupplyClient();
  return useQuery({
    queryKey: [...tailoredSupplyQueryKey, 'targeting-lookup', filter],
    queryFn: () => client.listTargetingTailoredSupplies(filter),
  });
}

export function useTailoredSupplyAppsQuery(filter: TailoredSupplyAppsFilter) {
  const client = useTailoredSupplyClient();
  return useQuery({
    queryKey: [...tailoredSupplyQueryKey, 'apps', filter],
    queryFn: () => client.listApps(filter),
  });
}

export function useTailoredSupplyDomainsQuery(filter: TailoredSupplyDomainsFilter) {
  const client = useTailoredSupplyClient();
  return useQuery({
    queryKey: [...tailoredSupplyQueryKey, 'domains', filter],
    queryFn: () => client.listDomains(filter),
  });
}

export function useTailoredSupplyReferences() {
  const client = useTailoredSupplyClient();
  return {
    publishers: useQuery({
      queryKey: [...tailoredSupplyQueryKey, 'publishers'],
      queryFn: client.listPublishers,
    }),
    platforms: useQuery({
      queryKey: [...tailoredSupplyQueryKey, 'platforms'],
      queryFn: client.listPlatforms,
    }),
    iabCategories: useQuery({
      queryKey: [...tailoredSupplyQueryKey, 'iab-categories'],
      queryFn: client.listIabCategories,
    }),
  };
}

export function useTailoredSupplyTableReferences() {
  const client = useTailoredSupplyClient();
  return {
    platforms: useQuery({
      queryKey: [...tailoredSupplyQueryKey, 'platforms'],
      queryFn: client.listPlatforms,
    }),
    iabCategories: useQuery({
      queryKey: [...tailoredSupplyQueryKey, 'iab-categories'],
      queryFn: client.listIabCategories,
    }),
  };
}

export function useTailoredSupplyMutation() {
  const client = useTailoredSupplyClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TailoredSupplyCreatePayload) => client.createTailoredSupply(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tailoredSupplyListsQueryKey }),
  });
}
